import { Upload } from "@tamagui/lucide-icons";
import type { ImagePickerResult } from "expo-image-picker";
import { type ReactNode, useRef, useState } from "react";
import { Platform } from "react-native";
import { YStack } from "tamagui";

import {
  type PrescriptionAnalysisResult,
  PrescriptionAnalysisStatus,
  usePrescriptionAnalysis,
} from "@/ai-doctor/prescription-scan/usePrescriptionAnalysis";
import type { DosespotMedication } from "@/api/generated/models";
import {
  useSendNoriMessage,
  useSendRxInput,
} from "@/api/mutations/conversation";
import {
  Button,
  LoadingSpinner,
  Paragraph,
  RadioGroup,
  Text,
} from "@/components";
import PanelHeader from "@/conversation/AskUserQuestionsPanel/PanelHeader";
import { isMobileWeb } from "@/utils/web";

const noop = () => {};

// OpenAI-supported image formats — excludes HEIC/HEIF (mirrors PrescriptionScan).
const WEB_ACCEPTED_IMAGE_TYPES = "image/jpeg,image/png,image/gif,image/webp";

// When the photo can't be resolved to a medication (parse failed, or the patient declines to upload),
// nudge the flow forward: the generalist's PHOTO_OFFER stage sees no selection and escalates to a live
// visit on its next turn. The text only shows in the transcript; the backend keys off the absent med.
const NO_MATCH_RX_INPUT = "I couldn't find my prescription in a photo.";
const DECLINE_RX_INPUT = "I don't have a photo of my prescription to upload.";

const medName = (med: DosespotMedication): string =>
  med.prescription_details.display_name ??
  med.prescription_details.generic_drug_name ??
  "";

/**
 * The `photo_upload` rx card body. Reuses the prescription-scan parse flow
 * (`usePrescriptionAnalysis` → `ParsePrescriptionWorkflow`), restyled to fit the inline rx panel.
 * On a confirmed/selected medication it sends `rx_renewal_selected_medication`, which the orchestrator
 * routes back into the active generalist flow at PHOTO_OFFER; a no-match sends a plain rx input so the
 * flow escalates.
 */
export default function RxPhotoUpload({
  conversationId,
  onWaiting,
  prompt,
}: {
  conversationId: string;
  onWaiting: () => void;
  /** The backend photo-offer question, shown as the header on the upload step. */
  prompt: string;
}) {
  const { analyze, resetUpload, result, isPending } = usePrescriptionAnalysis();
  const { sendNoriMessage } = useSendNoriMessage(conversationId);
  const { sendRxInput } = useSendRxInput(conversationId);

  const [picked, setPicked] = useState("");
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const isDesktop = !isMobileWeb();

  const onPhotoCaptured = (uri: string) => {
    setPicked("");
    analyze(uri);
  };

  const tryAgain = () => {
    resetUpload();
    setPicked("");
  };

  const confirmMedication = (medication: DosespotMedication) => {
    onWaiting();
    sendNoriMessage([medication], { type: "rx_renewal_selected_medication" });
  };

  const escalate = (message: string) => {
    onWaiting();
    sendRxInput(message);
  };

  const openPicker = async () => {
    if (Platform.OS === "web" && isDesktop) {
      fileInputRef.current?.click();
      return;
    }
    // Load the native picker only when the patient actually opens it — keeps expo-image-picker out of
    // the module graph for anything that merely renders this card (e.g. the rx panel + its tests).
    const ImagePicker = await import("expo-image-picker");
    let res: ImagePickerResult;
    try {
      res = await ImagePicker.launchCameraAsync({
        mediaTypes: ImagePicker.MediaTypeOptions.Images,
        quality: 1,
      });
    } catch {
      alert("Please upload a JPEG, PNG, GIF, or WebP image.");
      return;
    }
    if (!res.canceled && res.assets[0]) onPhotoCaptured(res.assets[0].uri);
  };

  const onFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!WEB_ACCEPTED_IMAGE_TYPES.split(",").includes(file.type)) {
      alert(
        `Unsupported file type: ${file.type || "unknown"}. Please upload a JPEG, PNG, GIF, or WebP image.`,
      );
      e.target.value = "";
      return;
    }
    onPhotoCaptured(URL.createObjectURL(file));
  };

  const webFileInput = Platform.OS === "web" && isDesktop && (
    <input
      ref={fileInputRef}
      type="file"
      accept={WEB_ACCEPTED_IMAGE_TYPES}
      style={{ display: "none" }}
      onChange={onFileChange}
    />
  );

  if (isPending) {
    return (
      <YStack
        alignItems="center"
        justifyContent="center"
        gap="$150"
        paddingHorizontal="$150"
        paddingVertical="$300"
      >
        <LoadingSpinner size="$800" />
        <Text semibold size="$sm">
          Reading your label…
        </Text>
      </YStack>
    );
  }

  const { title, node } = bodyForResult({
    result,
    prompt,
    picked,
    setPicked,
    confirmMedication,
    escalate,
    tryAgain,
    openPicker,
  });

  return (
    <>
      {webFileInput}
      <PanelHeader
        question={title}
        index={0}
        total={1}
        onPrev={noop}
        onNext={noop}
      />
      <YStack paddingHorizontal="$150" paddingBottom="$200" gap="$150">
        {node}
      </YStack>
    </>
  );
}

function bodyForResult({
  result,
  prompt,
  picked,
  setPicked,
  confirmMedication,
  escalate,
  tryAgain,
  openPicker,
}: {
  result: PrescriptionAnalysisResult | null;
  prompt: string;
  picked: string;
  setPicked: (v: string) => void;
  confirmMedication: (med: DosespotMedication) => void;
  escalate: (message: string) => void;
  tryAgain: () => void;
  openPicker: () => void;
}): { title: string; node: ReactNode } {
  // No parse result yet (nothing captured, or after "Try again"): the upload affordance, plus a way to
  // decline — which ends the async renewal and routes the patient to a live visit.
  if (!result) {
    return {
      title: prompt,
      node: (
        <>
          <Button
            mode="primary"
            icon={Upload}
            onPress={openPicker}
            dataTrackingId="rx-photo-upload-button"
          >
            Upload photo
          </Button>
          <Button
            mode="tertiary"
            onPress={() => escalate(DECLINE_RX_INPUT)}
            dataTrackingId="rx-photo-decline-button"
          >
            No thanks
          </Button>
        </>
      ),
    };
  }

  if (result.status === PrescriptionAnalysisStatus.Success) {
    return {
      title: "Please confirm your medication",
      node: (
        <>
          <YStack gap="$50">
            <Text size="$sm">
              Medication: {result.medication_name.toUpperCase()}
            </Text>
            {!!result.dosage && <Text size="$sm">Dosage: {result.dosage}</Text>}
            {!!result.directions && (
              <Text size="$sm">Directions: {result.directions}</Text>
            )}
          </YStack>
          <Button
            mode="primary"
            onPress={() => confirmMedication(result.medication)}
            dataTrackingId="rx-photo-confirm-button"
          >
            Confirm medication
          </Button>
          <Button
            mode="tertiary"
            onPress={tryAgain}
            dataTrackingId="rx-photo-try-again-button"
          >
            Try again
          </Button>
        </>
      ),
    };
  }

  if (result.status === PrescriptionAnalysisStatus.Unreadable) {
    const selected = result.medications.find(
      (m) => String(m.prescription_details.dispensable_drug_id) === picked,
    );
    return {
      title: "We couldn't read that label",
      node: (
        <>
          <Paragraph size="$sm" color="$textSecondary">
            We matched your prescription to the following. Which one is it?
          </Paragraph>
          <RadioGroup
            dataTrackingId="rx-photo-unreadable-option"
            value={picked}
            onValueChange={setPicked}
            borderless
          >
            {result.medications.map((med) => {
              const id = String(med.prescription_details.dispensable_drug_id);
              return (
                <RadioGroup.Item key={id} id={`rx-photo-med-${id}`} value={id}>
                  {medName(med)}
                </RadioGroup.Item>
              );
            })}
          </RadioGroup>
          <Button
            mode="primary"
            disabled={!selected}
            onPress={() => selected && confirmMedication(selected)}
            dataTrackingId="rx-photo-unreadable-confirm-button"
          >
            Continue
          </Button>
          <Button
            mode="tertiary"
            onPress={tryAgain}
            dataTrackingId="rx-photo-unreadable-try-again-button"
          >
            Try again
          </Button>
        </>
      ),
    };
  }

  // NoMatch
  return {
    title: "No match found",
    node: (
      <>
        <Paragraph size="$sm" color="$textSecondary">
          {result.error ?? "We couldn't match that prescription in our system."}{" "}
          A clinician can review it and help you out.
        </Paragraph>
        <Button
          mode="primary"
          onPress={() => escalate(NO_MATCH_RX_INPUT)}
          dataTrackingId="rx-photo-no-match-continue-button"
        >
          Talk to a clinician
        </Button>
        <Button
          mode="tertiary"
          onPress={tryAgain}
          dataTrackingId="rx-photo-no-match-try-again-button"
        >
          Try again
        </Button>
      </>
    ),
  };
}
